import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  ArrowsPointingOutIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import {
  getAttempt,
  submitAnswer as submitAnswerAPI,
  submitAttempt as submitAttemptAPI,
} from "../../services/Attempts/AttemptService";
import useExamSession from "../../hooks/useExamSession";
import CountdownTimer from "../../components/ExamTaking/CountdownTimer";
import QuestionNavPanel from "../../components/ExamTaking/QuestionNavPanel";
import QuestionDisplay from "../../components/ExamTaking/QuestionDisplay";
import ProgressBar from "../../components/ExamTaking/ProgressBar";
import MobileNavDrawer from "../../components/ExamTaking/MobileNavDrawer";
import SubmitConfirmModal from "../../components/ExamTaking/SubmitConfirmModal";
import ExitExamModal from "../../components/ExamTaking/ExitExamModal";
import Calculator from "../../components/ExamTaking/Calculator";
import PeriodicTable from "../../components/ExamTaking/PeriodicTable";
import EyeBreakOverlay from "../../components/ExamTaking/EyeBreakOverlay";
import PassagePane from "../../components/ExamTaking/PassagePane";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import KaTeXComponent from "../../components/KaTeXComponent/KaTeXComponent";
import { Button } from "../../components/forms/Buttons/Button";
import OverflowMenu from "../../components/ui/OverflowMenu";
import {
  getAttemptStatusText,
  isCanceledError,
  resolveExamErrorText,
} from "../../utils/examError";
import { IS_TOUCH_DEVICE, HAS_FULLSCREEN_API } from "../../utils/deviceDetection";

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

const buildApiUrl = (path) => {
  const normalizedPath = path.replace(/^\/+/, "");
  return API_BASE_URL ? `${API_BASE_URL}/${normalizedPath}` : null;
};

const DEFAULT_ADAPTER = {
  fetchAttempt: (uuid, signal) => getAttempt({ uuid, signal }),
  submitAnswer: (uuid, data, signal) =>
    submitAnswerAPI({ uuid, data, signal }),
  submitAttempt: (uuid, signal, data) => submitAttemptAPI({ uuid, data, signal }),
  getExitSubmitRequest: ({ attemptUuid, completionReason }) => {
    const accessToken = localStorage.getItem("accessTkn");
    const url = buildApiUrl(`/exams/attempts/${attemptUuid}/submit/`);

    if (!attemptUuid || !accessToken || !url) {
      return null;
    }

    return {
      method: "POST",
      url,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ completion_reason: completionReason || "disconnected" }),
    };
  },
};

// A full-page card in place of the exam: the fullscreen gate.
function GateScreen({ icon: Icon, tone, title, description, children }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-slate-50 px-4 py-8">
      <div className="flex w-full max-w-lg flex-col items-center gap-8 rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center shadow-sm motion-safe:animate-fade-in sm:px-10">
        <div className="flex flex-col items-center gap-4">
          <span className={`flex h-16 w-16 items-center justify-center rounded-2xl ring-1 ring-inset ${tone}`}>
            <Icon className="h-8 w-8" aria-hidden="true" />
          </span>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h2>
          <p className="text-sm leading-6 text-slate-500">{description}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ExamSession({
  apiAdapter = DEFAULT_ADAPTER,
  resultPath,
  exitPath = "/my-attempts",
}) {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [attemptData, setAttemptData] = useState(null);
  const [examTests, setExamTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showFullscreenOverlay, setShowFullscreenOverlay] = useState(false);
  const [fullscreenReady, setFullscreenReady] = useState(IS_TOUCH_DEVICE || !HAS_FULLSCREEN_API);

  const submittedRef = useRef(false);
  const allowNavigationRef = useRef(false);
  const hardExitSubmittedRef = useRef(false);
  const focusLossCountRef = useRef(0);
  const lastFocusLossTimestampRef = useRef(0);
  const visibilityLostAtRef = useRef(null);
  const awayTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const [awayCountdown, setAwayCountdown] = useState(null);
  const fullscreenExitCountRef = useRef(0);
  // Lenient until the attempt says otherwise: a page that never loaded must
  // not auto-submit an exam that is not strict.
  const strictRef = useRef(false);
  const attemptLoadedAtRef = useRef(null);
  const breakActiveRef = useRef(false);
  const splitRef = useRef(false);
  const [away, setAway] = useState(false);
  const [split, setSplit] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const mainRef = useRef(null);
  const questionColumnRef = useRef(null);
  // Where the student left each passage's text (by passage uuid) while the page is open.
  const passageScrollRef = useRef(new Map());

  const navigateSafely = useCallback(
    (path, options) => {
      allowNavigationRef.current = true;
      navigate(path, options);
    },
    [navigate]
  );

  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;

    apiAdapter
      .fetchAttempt(uuid, controller.signal)
      .then((data) => {
        if (!isCurrent) return;

        if (data.status && data.status !== "IN_PROGRESS") {
          toast.info(
            t("exam_taking.exam_status_redirect", {
              status: getAttemptStatusText(t, data.status),
            })
          );

          if (data.status === "GRADED") {
            navigateSafely(resultPath || `/exam/attempt/${uuid}/result`, {
              replace: true,
            });
          } else {
            navigateSafely(exitPath, { replace: true });
          }
          return;
        }
        strictRef.current = data.strict_proctoring !== false;
        attemptLoadedAtRef.current = Date.now();
        setAttemptData(data);
        setExamTests(data.exam_tests || []);
      })
      .catch((error) => {
        if (!isCurrent || isCanceledError(error)) {
          return;
        }
        toast.error(
          resolveExamErrorText(t, error, "exam_taking.failed_to_load_exam_details")
        );
      })
      .finally(() => {
        if (!isCurrent) return;
        setLoading(false);
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [uuid, apiAdapter, navigateSafely, resultPath, exitPath, t]);

  const handleSubmitAnswer = useCallback(
    (data) => apiAdapter.submitAnswer(uuid, data),
    [uuid, apiAdapter]
  );

  const handleBreakChange = useCallback((active) => {
    breakActiveRef.current = active;
  }, []);

  const session = useExamSession(attemptData, handleSubmitAnswer, examTests);

  const currentTestUuid = session.currentQuestion?.test_uuid;
  const currentTest = currentTestUuid
    ? examTests.find((et) => et.test?.uuid === currentTestUuid)?.test || null
    : null;

  // Context questions: from xl the passage stands beside the question and each scrolls on its own;
  // below xl it stands above the question and the page scrolls.
  const passage = session.currentPassage;
  const runKey = session.currentRun?.key;
  const calculatorShown = calculatorOpen && Boolean(currentTest?.calculator_is_allowed);

  // Entering or leaving a passage's questions changes what stands above the question: start at the top.
  useLayoutEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [runKey]);

  // Beside a passage the question column scrolls on its own, and each question starts at its top
  // (elsewhere the column does not scroll and this does nothing).
  useLayoutEffect(() => {
    if (questionColumnRef.current) questionColumnRef.current.scrollTop = 0;
  }, [session.currentIndex]);

  const handleSubmit = useCallback(async (completionReason) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const data = typeof completionReason === 'string' ? { completion_reason: completionReason } : {};
      await apiAdapter.submitAttempt(uuid, undefined, data);
      navigateSafely(resultPath || `/exam/attempt/${uuid}/result`, {
        replace: true,
      });
    } catch (error) {
      toast.error(
        resolveExamErrorText(t, error, "exam_taking.failed_to_submit_exam")
      );
      submittedRef.current = false;
    } finally {
      setSubmitting(false);
      setShowSubmitModal(false);
    }
  }, [uuid, apiAdapter, navigateSafely, resultPath, t]);

  const submitOnHardExit = useCallback(() => {
    if (
      !strictRef.current ||
      submittedRef.current ||
      allowNavigationRef.current ||
      hardExitSubmittedRef.current
    ) {
      return;
    }

    hardExitSubmittedRef.current = true;

    const exitRequest = apiAdapter.getExitSubmitRequest?.({ attemptUuid: uuid, completionReason: "disconnected" });
    if (exitRequest?.url) {
      fetch(exitRequest.url, {
        method: exitRequest.method || "POST",
        headers: exitRequest.headers,
        body: exitRequest.body || JSON.stringify({ completion_reason: "disconnected" }),
        keepalive: true,
        credentials: "same-origin",
      }).catch(() => {});
      return;
    }

    apiAdapter.submitAttempt(uuid, undefined, { completion_reason: "disconnected" }).catch(() => {});
  }, [apiAdapter, uuid]);

  const handleConfirmExit = useCallback(async () => {
    if (submittedRef.current || exiting) return;

    setExiting(true);

    try {
      await apiAdapter.submitAttempt(uuid, undefined, { completion_reason: "submitted" });
      submittedRef.current = true;
      toast.info(t("exam_taking.exit_submit_success"));
      setShowExitModal(false);
      navigateSafely(exitPath, { replace: true });
    } catch (error) {
      toast.error(
        resolveExamErrorText(t, error, "exam_taking.failed_to_submit_exam")
      );
    } finally {
      setExiting(false);
    }
  }, [apiAdapter, exiting, exitPath, navigateSafely, t, uuid]);

  const handleTimerExpire = useCallback(() => {
    if (submittedRef.current) return;
    toast.warning(t("exam_taking.time_expired_auto_submit"));
    handleSubmit("time_expired");
  }, [handleSubmit, t]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (submittedRef.current || allowNavigationRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };

    const handlePageHide = () => {
      submitOnHardExit();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [submitOnHardExit]);

  // Pull-to-refresh would reload the exam page on a phone.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtml = html.style.overscrollBehavior;
    const previousBody = body.style.overscrollBehavior;
    html.style.overscrollBehavior = "none";
    body.style.overscrollBehavior = "none";
    return () => {
      html.style.overscrollBehavior = previousHtml;
      body.style.overscrollBehavior = previousBody;
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (submittedRef.current || allowNavigationRef.current) {
        return;
      }

      setShowExitModal(true);
      window.history.pushState({ examGuard: true }, "", window.location.href);
    };

    window.history.pushState({ examGuard: true }, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (submittedRef.current || allowNavigationRef.current) {
        return;
      }

      const key = event.key.toLowerCase();

      const isReloadShortcut =
        key === "f5" || ((event.ctrlKey || event.metaKey) && key === "r");
      const isDevToolsShortcut =
        key === "f12" ||
        (event.ctrlKey && event.shiftKey && (key === "i" || key === "j" || key === "c")) ||
        (event.metaKey && event.altKey && key === "i");
      const isViewSourceShortcut =
        (event.ctrlKey || event.metaKey) && key === "u";
      const isPrintShortcut =
        (event.ctrlKey || event.metaKey) && key === "p";

      if (isDevToolsShortcut || isViewSourceShortcut || isPrintShortcut) {
        event.preventDefault();
        return;
      }

      if (!isReloadShortcut) {
        return;
      }

      event.preventDefault();
      setShowExitModal(true);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // The answer/navigation shortcuts act on the question on screen, so they are
  // off before the exam starts and while a modal or the overlay covers it.
  const shortcutsDisabled =
    !attemptData ||
    !fullscreenReady ||
    showSubmitModal ||
    showExitModal ||
    showMobileNav ||
    showFullscreenOverlay;

  useEffect(() => {
    if (shortcutsDisabled) return;

    const handleNavKeyDown = (event) => {
      if (submittedRef.current || allowNavigationRef.current) return;

      const tag = event.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      // A key pressed in a dialog, the "⋯" menu, the calculator or the periodic table was not meant for the question.
      if (event.target.closest?.("[role=dialog], [role=menu], [data-exam-calculator], [data-exam-tool]")) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const key = event.key;

      if (key === "ArrowLeft") {
        event.preventDefault();
        session.prevQuestion();
        return;
      }
      if (key === "ArrowRight") {
        event.preventDefault();
        session.nextQuestion();
        return;
      }
      if (key === "f" || key === "F") {
        event.preventDefault();
        if (session.currentQuestion) {
          session.toggleFlag(session.currentQuestion.attempt_question_uuid);
        }
        return;
      }

      const num = parseInt(key, 10);
      if (num >= 1 && num <= 9 && session.currentQuestion?.options) {
        const option = session.currentQuestion.options[num - 1];
        if (option) {
          event.preventDefault();
          session.selectAnswer(
            session.currentQuestion.attempt_question_uuid,
            option.uuid
          );
        }
      }
    };

    window.addEventListener("keydown", handleNavKeyDown);
    return () => window.removeEventListener("keydown", handleNavKeyDown);
  }, [session, shortcutsDisabled]);

  useEffect(() => {
    // Under an eye break no key may act on the hidden page: digits pick answers,
    // Tab+Space presses focused options, F5 opens the submit dialog.
    const blockKeys = (event) => {
      if (!breakActiveRef.current) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    window.addEventListener("keydown", blockKeys, true);
    return () => window.removeEventListener("keydown", blockKeys, true);
  }, []);

  const startAwayCountdown = useCallback(() => {
    if (!strictRef.current) return;
    const AWAY_TIMEOUT = 25;
    setAwayCountdown(AWAY_TIMEOUT);
    clearInterval(countdownIntervalRef.current);
    clearTimeout(awayTimerRef.current);

    countdownIntervalRef.current = setInterval(() => {
      setAwayCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    awayTimerRef.current = setTimeout(() => {
      clearInterval(countdownIntervalRef.current);
      setAwayCountdown(null);
      if (!submittedRef.current) {
        handleSubmit("disconnected");
      }
    }, AWAY_TIMEOUT * 1000);
  }, [handleSubmit]);

  const clearAwayCountdown = useCallback(() => {
    clearTimeout(awayTimerRef.current);
    clearInterval(countdownIntervalRef.current);
    setAwayCountdown(null);
  }, []);

  const DEBOUNCE_MS = 500;
  const TOUCH_GRACE_MS = 3000;

  const countStrike = useCallback(
    (toastKey) => {
      if (!strictRef.current) return;
      focusLossCountRef.current += 1;
      if (focusLossCountRef.current >= 2) {
        clearAwayCountdown();
        toast.error(t(toastKey));
        handleSubmit("disconnected");
        return;
      }
      toast.warning(t("exam_taking.tab_switch_warning"));
      startAwayCountdown();
    },
    [clearAwayCountdown, handleSubmit, startAwayCountdown, t]
  );

  const handleFocusLoss = useCallback(
    () => {
      if (submittedRef.current || allowNavigationRef.current) return;
      setAway(true);

      const now = Date.now();
      if (now - lastFocusLossTimestampRef.current < DEBOUNCE_MS) return;
      lastFocusLossTimestampRef.current = now;

      visibilityLostAtRef.current = now;

      if (IS_TOUCH_DEVICE) {
        // On touch devices defer counting — just start the away countdown
        startAwayCountdown();
      } else {
        countStrike("exam_taking.tab_switch_auto_submit");
      }
    },
    [countStrike, startAwayCountdown]
  );

  const handleFocusRegain = useCallback(() => {
    if (submittedRef.current || allowNavigationRef.current) return;
    setAway(false);

    clearAwayCountdown();

    if (IS_TOUCH_DEVICE && visibilityLostAtRef.current !== null) {
      const elapsed = Date.now() - visibilityLostAtRef.current;
      visibilityLostAtRef.current = null;
      if (elapsed >= TOUCH_GRACE_MS) {
        countStrike("exam_taking.tab_switch_auto_submit");
      }
    }
  }, [clearAwayCountdown, countStrike]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        handleFocusLoss();
      } else {
        handleFocusRegain();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearAwayCountdown();
    };
  }, [handleFocusLoss, handleFocusRegain, clearAwayCountdown]);

  useEffect(() => {
    if (IS_TOUCH_DEVICE) return;

    const SPLIT_THRESHOLD = 0.6;

    const handleResize = () => {
      if (submittedRef.current || allowNavigationRef.current) return;

      const isSplit = window.outerWidth / window.screen.width < SPLIT_THRESHOLD;
      setSplit(isSplit);
      // Resize fires continuously while dragging: submit once per split.
      if (isSplit && !splitRef.current && strictRef.current) {
        toast.error(t("exam_taking.split_screen_detected"));
        handleSubmit("disconnected");
      }
      splitRef.current = isSplit;
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleSubmit, t]);

  useEffect(() => {
    const onBlur = () => handleFocusLoss();
    const onFocus = () => handleFocusRegain();

    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      clearAwayCountdown();
    };
  }, [handleFocusLoss, handleFocusRegain, clearAwayCountdown]);

  useEffect(() => {
    const block = (e) => e.preventDefault();
    document.addEventListener("contextmenu", block);
    document.addEventListener("copy", block);
    return () => {
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("copy", block);
    };
  }, []);

  useEffect(() => {
    if (IS_TOUCH_DEVICE || !HAS_FULLSCREEN_API) return;
    if (!attemptData || !fullscreenReady) return;

    const handleFullscreenChange = () => {
      if (submittedRef.current || allowNavigationRef.current) return;
      if (document.fullscreenElement) {
        setShowFullscreenOverlay(false);
        return;
      }

      fullscreenExitCountRef.current += 1;
      if (!strictRef.current || fullscreenExitCountRef.current === 1) {
        setShowFullscreenOverlay(true);
        return;
      }

      toast.error(t("exam_taking.fullscreen_exit_auto_submit"));
      handleSubmit("disconnected");
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      if (document.fullscreenElement) {
        document.exitFullscreen?.().catch(() => {});
      }
    };
  }, [attemptData, fullscreenReady, handleSubmit, t]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    const previousHtmlTranslate = html.getAttribute("translate");
    const previousBodyTranslate = body.getAttribute("translate");

    html.setAttribute("translate", "no");
    body.setAttribute("translate", "no");
    html.classList.add("notranslate");
    body.classList.add("notranslate");

    const existingMeta = document.querySelector('meta[name="google"]');
    const previousMetaContent = existingMeta?.getAttribute("content") || null;

    let injectedMeta = null;
    if (!existingMeta) {
      injectedMeta = document.createElement("meta");
      injectedMeta.setAttribute("name", "google");
      document.head.appendChild(injectedMeta);
      injectedMeta.setAttribute("content", "notranslate");
    } else {
      existingMeta.setAttribute("content", "notranslate");
    }

    return () => {
      if (previousHtmlTranslate === null) {
        html.removeAttribute("translate");
      } else {
        html.setAttribute("translate", previousHtmlTranslate);
      }

      if (previousBodyTranslate === null) {
        body.removeAttribute("translate");
      } else {
        body.setAttribute("translate", previousBodyTranslate);
      }

      html.classList.remove("notranslate");
      body.classList.remove("notranslate");

      if (injectedMeta) {
        injectedMeta.remove();
      } else if (existingMeta) {
        if (previousMetaContent === null) {
          existingMeta.removeAttribute("content");
        } else {
          existingMeta.setAttribute("content", previousMetaContent);
        }
      }
    };
  }, []);

  if (loading) return <SimpleLoader className="h-8 w-8" />;
  if (!attemptData) {
    return <div className="p-6 text-sm text-slate-500">{t("exam_taking.exam_not_found")}</div>;
  }

  // The attempt sends {exam: {uuid, name}}; exam_name is kept for older payloads.
  const examName = attemptData.exam?.name || attemptData.exam_name;

  if (!fullscreenReady) {
    return (
      <GateScreen
        icon={ArrowsPointingOutIcon}
        tone="bg-blue-50 text-blue-600 ring-blue-100"
        title={examName || t("exam_taking.start_exam")}
        description={t("exam_taking.fullscreen_required")}
      >
        <button
          type="button"
          onClick={() => {
            document.documentElement.requestFullscreen?.().catch(() => {});
            setFullscreenReady(true);
          }}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition duration-200 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 active:scale-[0.98]"
        >
          <ArrowsPointingOutIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          {t("exam_taking.enter_fullscreen_start")}
        </button>
      </GateScreen>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-slate-50 notranslate" translate="no">
      <header className="sticky top-0 z-10 flex-shrink-0 border-b border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {/* Phones: the question list opens in a drawer (from md up it stands on the left). A grid, not "☰",
                which elsewhere in the app opens the menu. */}
            <button
              type="button"
              onClick={() => setShowMobileNav(true)}
              className="-ml-2 inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 md:hidden"
              aria-label={t("exam_taking.question_nav")}
              title={t("exam_taking.question_nav")}
            >
              <Squares2X2Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <h1 className="min-w-0 truncate text-base font-semibold tracking-tight text-slate-900 sm:text-lg">
              {examName || t("exam_taking.exam")}
            </h1>
          </div>

          {attemptData.remaining_seconds != null && (
            <CountdownTimer
              remainingSeconds={attemptData.remaining_seconds}
              onExpire={handleTimerExpire}
            />
          )}
          {/* One main action. Leaving submits the attempt too, so it waits in "⋯" (red, and its dialog still
              asks). Phones: the two in a row of their own. */}
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <Button
              text={t("exam_taking.submit_exam")}
              color="primary"
              onClick={() => setShowSubmitModal(true)}
              className="!h-11 min-w-0 flex-1 !px-4 text-sm sm:flex-none md:!h-10"
            />
            <OverflowMenu
              label={t("exam_taking.more_actions")}
              testId="exam-actions"
              items={[
                {
                  key: "exit",
                  label: t("exam_taking.exit_exam"),
                  Icon: ArrowRightOnRectangleIcon,
                  danger: true,
                  onClick: () => setShowExitModal(true),
                },
              ]}
            />
          </div>
        </div>
        <ProgressBar answered={session.answeredCount} total={session.totalCount} />
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden w-64 flex-shrink-0 overflow-y-auto border-r border-slate-200 bg-white p-5 md:block">
          <QuestionNavPanel
            questions={session.questions}
            currentIndex={session.currentIndex}
            answeredMap={session.answeredMap}
            testSections={session.testSections}
            flaggedSet={session.flaggedSet}
            runByIndex={session.runByIndex}
            onNavigate={session.goToQuestion}
          />
        </aside>

        <main
          ref={mainRef}
          className={
            passage
              ? "flex-1 overflow-y-auto px-4 pb-24 pt-6 sm:px-6 lg:pt-10 xl:grid xl:grid-cols-[repeat(2,minmax(0,50rem))] xl:grid-rows-1 xl:justify-center xl:gap-6 xl:overflow-hidden xl:pb-6"
              : "flex-1 overflow-y-auto px-4 pb-24 pt-6 sm:px-6 lg:pt-10"
          }
        >
          {passage && (
            <PassagePane
              key={runKey}
              run={session.currentRun}
              passage={passage}
              scrollPositions={passageScrollRef.current}
              className="mx-auto mb-4 max-w-3xl xl:mx-0 xl:mb-0 xl:max-w-none"
            />
          )}
          {/* The question column stays the second child with or without a passage, so the open test
              information keeps its state. The open calculator covers the bottom right: the extra
              padding lets the options scroll above it. */}
          <div
            ref={questionColumnRef}
            className={
              passage
                ? `mx-auto max-w-3xl xl:mx-0 xl:min-h-0 xl:max-w-none xl:overflow-y-auto xl:px-1 ${
                    calculatorShown ? "pb-[30rem]" : "xl:pb-24"
                  }`
                : "mx-auto max-w-3xl min-[1920px]:max-w-4xl"
            }
          >
            {session.currentRun && !passage && (
              <div
                role="status"
                className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              >
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden="true" />
                {t("exam_taking.passage.unavailable")}
              </div>
            )}

            <p className="mb-4 text-sm font-medium text-slate-500">
              {t("exam_taking.question")}{" "}
              <span className="font-semibold tabular-nums text-slate-900">{session.currentIndex + 1}</span>
              <span className="tabular-nums"> / {session.totalCount}</span>
            </p>

            {currentTest?.additional_information && (
              <details className="group mb-4 overflow-hidden rounded-xl border border-blue-100 bg-blue-50/60">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 transition-colors duration-200 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
                  <span className="flex items-center gap-2 text-sm font-medium text-blue-900">
                    <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden="true" />
                    {t("exam_taking.additional_information")}
                  </span>
                  <span className="flex items-center gap-1 text-xs font-medium text-blue-700">
                    <span className="group-open:hidden">
                      {t("exam_taking.expand")}
                    </span>
                    <span className="hidden group-open:inline">
                      {t("exam_taking.collapse")}
                    </span>
                    <ChevronDownIcon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 group-open:rotate-180" aria-hidden="true" />
                  </span>
                </summary>
                <div className="max-w-none border-t border-blue-100 bg-white px-4 py-4 text-sm leading-6 text-slate-700">
                  <KaTeXComponent text={currentTest.additional_information} />
                </div>
              </details>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
              <QuestionDisplay
                question={session.currentQuestion}
                selectedAnswerUuid={
                  session.answeredMap[
                    session.currentQuestion?.attempt_question_uuid
                  ]
                }
                onSelectAnswer={session.selectAnswer}
                isFlagged={session.flaggedSet.has(
                  session.currentQuestion?.attempt_question_uuid
                )}
                onToggleFlag={session.toggleFlag}
              />
            </div>

            {/* "Next" is the page's one accent; "Back" stays quiet. */}
            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={session.prevQuestion}
                disabled={session.currentIndex === 0}
                aria-label={t("buttons.back")}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm font-medium text-slate-600 transition duration-200 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                {t("buttons.back")}
              </button>
              <button
                type="button"
                onClick={session.nextQuestion}
                disabled={session.currentIndex === session.totalCount - 1}
                aria-label={t("buttons.next")}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:hover:bg-blue-600 sm:px-5"
              >
                {t("buttons.next")}
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>

          </div>
        </main>
      </div>

      <MobileNavDrawer
        isOpen={showMobileNav}
        onClose={() => setShowMobileNav(false)}
        questions={session.questions}
        currentIndex={session.currentIndex}
        answeredMap={session.answeredMap}
        testSections={session.testSections}
        flaggedSet={session.flaggedSet}
        runByIndex={session.runByIndex}
        onNavigate={session.goToQuestion}
      />

      <SubmitConfirmModal
        isOpen={showSubmitModal}
        onConfirm={handleSubmit}
        onCancel={() => setShowSubmitModal(false)}
        totalQuestions={session.totalCount}
        answeredQuestions={session.answeredCount}
        loading={submitting}
      />

      <ExitExamModal
        isOpen={showExitModal}
        onConfirm={handleConfirmExit}
        onCancel={() => setShowExitModal(false)}
        loading={exiting}
      />

      {currentTest?.calculator_is_allowed && <Calculator onOpenChange={setCalculatorOpen} />}
      {currentTest?.periodic_table_is_allowed && (
        <PeriodicTable calculatorAllowed={Boolean(currentTest.calculator_is_allowed)} calculatorOpen={calculatorShown} />
      )}

      {/* Soft mode: the frame shows while a violation lasts, so the proctor sees
          which screen to look at. Topmost, over the fullscreen prompt and eye break. */}
      {!strictRef.current && (away || split || showFullscreenOverlay) && (
        <div className="pointer-events-none fixed inset-0 z-[60] border-8 border-red-600">
          <div className="absolute left-1/2 top-0 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-b-lg bg-red-600 px-4 py-1 text-sm font-semibold text-white shadow-lg shadow-red-600/30">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {t("exam_taking.return_to_exam")}
          </div>
        </div>
      )}

      {awayCountdown !== null && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-wrap items-center justify-center gap-3 bg-red-600 px-4 py-3 text-white shadow-[0_-8px_24px_-8px_rgb(220_38_38/0.5)]">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm font-semibold tabular-nums">
            {t("exam_taking.away_countdown", { seconds: awayCountdown })}
          </span>
          <div className="h-2 w-32 overflow-hidden rounded-full bg-red-800/70 sm:ml-4">
            <div
              className="h-full rounded-full bg-white transition-all duration-1000"
              style={{ width: `${(awayCountdown / 25) * 100}%` }}
            />
          </div>
        </div>
      )}

      {showFullscreenOverlay && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-8 bg-slate-950 px-4">
          <div className="flex max-w-md flex-col items-center gap-4 px-4 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-400 text-3xl font-bold text-slate-950 ring-8 ring-amber-400/20">
              !
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">
              {t("exam_taking.fullscreen_exit_warning")}
            </h2>
            <p className="text-sm leading-6 text-slate-400">
              {strictRef.current
                ? t("exam_taking.fullscreen_exit_auto_submit")
                : t("exam_taking.violation_recorded")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              document.documentElement.requestFullscreen?.().catch(() => {});
            }}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 px-8 text-base font-semibold text-white shadow-lg shadow-blue-600/30 transition duration-200 hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
          >
            {t("exam_taking.fullscreen_return")}
          </button>
        </div>
      )}
      <EyeBreakOverlay
        attempt={attemptData}
        loadedAt={attemptLoadedAtRef.current}
        onActiveChange={handleBreakChange}
      />
    </div>
  );
}
